import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Paper,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Grid,
    Chip,
    Icon,
    Popover
} from '@material-ui/core';
import { Whatshot } from '@material-ui/icons';
import { Line } from 'react-chartjs-2';
import { colors } from '@material-ui/core';
import moment from 'moment';
import * as Actions from '../../store/actions';

const GpuMetricsChart = ({ clusterId, className, initialSelectedNode = 'all' }) => {
    const dispatch = useDispatch();
    const [chartData, setChartData] = useState(null);
    const [timeRange, setTimeRange] = useState('6h');
    const [selectedNode, setSelectedNode] = useState(initialSelectedNode);
    const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
    const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
    const [selectedGpuModel, setSelectedGpuModel] = useState(null);
    
    // Get cluster metrics history from Redux store
    const { 
        history: clusterMetricsHistory,
        loading,
        error 
    } = useSelector(({ clusters }) => clusters.cluster.clusterMetrics);
    
    // Get socket-based change flags for real-time updates
    const changedDbClusterHealth = useSelector(({ providers }) => providers.providers.changedDbClusterHealth);
    const changedDbClusters = useSelector(({ providers }) => providers.providers.changedDbClusters);
    const changedDbNodes = useSelector(({ providers }) => providers.providers.changedDbNodes);

    const timeRangeOptions = [
        { value: '1h', label: '1 Hour' },
        { value: '6h', label: '6 Hours' },
        { value: '24h', label: '24 Hours' },
        { value: '7d', label: '7 Days' },
        { value: '30d', label: '30 Days' }
    ];

    // Check URL parameters for pre-selected node on mount
    useEffect(() => {
        const hash = window.location.hash;
        if (hash.includes('selectedNode=')) {
            const urlParams = new URLSearchParams(hash.split('?')[1]);
            const nodeFromUrl = urlParams.get('selectedNode');
            if (nodeFromUrl) {
                setSelectedNode(decodeURIComponent(nodeFromUrl));
            }
        }
    }, []);

    // Get available nodes with GPUs from cluster metrics
    const getAvailableGpuNodes = () => {
        if (!clusterMetricsHistory || clusterMetricsHistory.length === 0) return [];
        
        const nodeSet = new Set();
        clusterMetricsHistory.forEach(metric => {
            if (metric.nodes) {
                metric.nodes.forEach(node => {
                    // Only include nodes that have available GPUs
                    if (node.system?.gpu?.available && node.system.gpu.gpus?.length > 0) {
                        nodeSet.add(node.hostname);
                    }
                });
            }
        });
        
        return Array.from(nodeSet).sort();
    };

    // Get detailed GPU information for a specific model
    const getGpuModelDetails = (modelName) => {
        if (!clusterMetricsHistory || clusterMetricsHistory.length === 0) return null;
        
        const latest = clusterMetricsHistory[0];
        if (!latest) return null;

        const modelGpus = [];
        let totalMemoryMB = 0;
        let totalUtilization = 0;
        let totalTemperature = 0;
        let minTemp = Infinity;
        let maxTemp = -Infinity;
        let nodeCount = 0;
        const nodesWithThisModel = new Set();

        if (latest.nodes && Array.isArray(latest.nodes)) {
            latest.nodes.forEach(node => {
                if (node.system?.gpu?.available && node.system.gpu.gpus?.length > 0) {
                    node.system.gpu.gpus.forEach(gpu => {
                        if ((gpu.name || 'Unknown') === modelName) {
                            modelGpus.push({
                                ...gpu,
                                nodeName: node.hostname
                            });
                            nodesWithThisModel.add(node.hostname);
                            totalMemoryMB += gpu.memory?.totalMB || 0;
                            totalUtilization += gpu.utilizationPercent || 0;
                            const temp = gpu.temperatureC || 0;
                            totalTemperature += temp;
                            if (temp > 0) {
                                minTemp = Math.min(minTemp, temp);
                                maxTemp = Math.max(maxTemp, temp);
                            }
                        }
                    });
                }
            });
        }

        if (modelGpus.length === 0) return null;

        return {
            modelName,
            count: modelGpus.length,
            nodes: Array.from(nodesWithThisModel),
            avgUtilization: totalUtilization / modelGpus.length,
            totalMemoryGB: (totalMemoryMB / modelGpus.length / 1024).toFixed(1), // Per GPU
            avgTemperature: totalTemperature / modelGpus.length,
            minTemperature: minTemp === Infinity ? 0 : minTemp,
            maxTemperature: maxTemp === -Infinity ? 0 : maxTemp,
            details: modelGpus
        };
    };

    // Handle popover open
    const handlePopoverOpen = (event, modelName) => {
        setPopoverAnchorEl(event.currentTarget);
        setSelectedGpuModel(modelName);
    };

    // Handle popover close
    const handlePopoverClose = () => {
        setPopoverAnchorEl(null);
        setSelectedGpuModel(null);
    };

    // Get current GPU summary stats
    const getCurrentGpuStats = () => {
        if (!clusterMetricsHistory || clusterMetricsHistory.length === 0) return null;
        
        const latest = clusterMetricsHistory[0]; // Most recent
        if (!latest) return null;

        let totalGpus = 0;
        let activeGpus = 0;
        let totalUtilization = 0;
        let totalMemoryUsed = 0;
        let totalMemoryTotal = 0;
        let totalTemperature = 0;
        const gpuModels = new Set();

        if (latest.nodes && Array.isArray(latest.nodes)) {
            latest.nodes.forEach(node => {
                if (node.system?.gpu?.available && node.system.gpu.gpus?.length > 0) {
                    node.system.gpu.gpus.forEach(gpu => {
                        totalGpus = totalGpus + 1;
                        gpuModels.add(gpu.name || 'Unknown');
                        
                        if (gpu.utilizationPercent > 0) {
                            activeGpus = activeGpus + 1;
                        }
                        
                        totalUtilization = totalUtilization + (gpu.utilizationPercent || 0);
                        totalMemoryUsed = totalMemoryUsed + (gpu.memory?.usedMB || 0);
                        totalMemoryTotal = totalMemoryTotal + (gpu.memory?.totalMB || 0);
                        totalTemperature = totalTemperature + (gpu.temperatureC || 0);
                    });
                }
            });
        }

        return {
            totalGpus,
            activeGpus,
            avgUtilization: totalGpus > 0 ? totalUtilization / totalGpus : 0,
            totalMemoryUsedGB: totalMemoryUsed / 1024,
            totalMemoryTotalGB: totalMemoryTotal / 1024,
            memoryUtilization: totalMemoryTotal > 0 ? (totalMemoryUsed / totalMemoryTotal) * 100 : 0,
            avgTemperature: totalGpus > 0 ? totalTemperature / totalGpus : 0,
            models: Array.from(gpuModels)
        };
    };

    // Process GPU metrics data for charting
    const processGpuMetricsData = (metrics) => {
        if (!metrics || metrics.length === 0) {
            setChartData(null);
            return;
        }

        // Sort metrics by timestamp
        const sortedMetrics = [...metrics].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        
        const timeLabels = [];
        const datasets = [];

        if (selectedNode === 'all') {
            // Show cluster-wide aggregated GPU data
            const utilizationData = [];
            const memoryData = [];
            const temperatureData = [];
            const activeGpuData = [];
            
            sortedMetrics.forEach(metric => {
                let timeLabel;
                if (timeRange === '1h' || timeRange === '6h') {
                    timeLabel = moment(metric.timestamp).format('HH:mm');
                } else if (timeRange === '24h') {
                    timeLabel = moment(metric.timestamp).format('MM/DD HH:mm');
                } else if (timeRange === '7d') {
                    timeLabel = moment(metric.timestamp).format('MM/DD');
                } else {
                    timeLabel = moment(metric.timestamp).format('MM/DD');
                }
                timeLabels.push(timeLabel);
                
                // Calculate cluster-wide GPU metrics
                let sumUtilization = 0; // Sum of all individual GPU utilizations
                let totalMemoryUsed = 0;
                let totalMemoryTotal = 0;
                let sumTemperature = 0;
                let totalGpus = 0;
                let activeGpus = 0;
                
                if (metric.nodes && Array.isArray(metric.nodes)) {
                    metric.nodes.forEach(node => {
                        if (node.system?.gpu?.available && node.system.gpu.gpus?.length > 0) {
                            node.system.gpu.gpus.forEach(gpu => {
                                totalGpus = totalGpus + 1;
                                // Sum individual GPU utilizations for cluster-wide percentage
                                sumUtilization = sumUtilization + (gpu.utilizationPercent || 0);
                                totalMemoryUsed = totalMemoryUsed + (gpu.memory?.usedMB || 0);
                                totalMemoryTotal = totalMemoryTotal + (gpu.memory?.totalMB || 0);
                                sumTemperature = sumTemperature + (gpu.temperatureC || 0);
                                
                                if ((gpu.utilizationPercent || 0) > 5) { // Consider >5% as "active"
                                    activeGpus = activeGpus + 1;
                                }
                            });
                        }
                    });
                }
                
                // For "All GPU Nodes": show percentage of total cluster GPU capacity being used
                // Example: 3 GPUs, 1 at 100% usage = 100/3 = 33.3% cluster utilization
                const clusterUtilizationPercent = totalGpus > 0 ? sumUtilization / totalGpus : 0;
                const clusterMemoryPercent = totalMemoryTotal > 0 ? (totalMemoryUsed / totalMemoryTotal) * 100 : 0;
                const avgTemperature = totalGpus > 0 ? sumTemperature / totalGpus : 0;
                
                utilizationData.push(clusterUtilizationPercent);
                memoryData.push(clusterMemoryPercent);
                temperatureData.push(avgTemperature);
                activeGpuData.push(activeGpus);
            });

            datasets.push(
                {
                    label: 'Cluster GPU Utilization (%)',
                    data: utilizationData,
                    borderColor: colors.orange[500],
                    backgroundColor: colors.orange[100],
                    fill: false,
                    pointRadius: 2,
                    pointHoverRadius: 4,
                    borderWidth: 2,
                    yAxisID: 'y'
                },
                {
                    label: 'Cluster Memory Utilization (%)',
                    data: memoryData,
                    borderColor: colors.purple[500],
                    backgroundColor: colors.purple[100],
                    fill: false,
                    pointRadius: 2,
                    pointHoverRadius: 4,
                    borderWidth: 2,
                    yAxisID: 'y'
                },
                {
                    label: 'Average Temperature (°C)',
                    data: temperatureData,
                    borderColor: colors.red[500],
                    backgroundColor: colors.red[100],
                    fill: false,
                    pointRadius: 2,
                    pointHoverRadius: 4,
                    borderWidth: 2,
                    yAxisID: 'y1'
                },
                {
                    label: 'Active GPUs',
                    data: activeGpuData,
                    borderColor: colors.green[500],
                    backgroundColor: colors.green[100],
                    fill: false,
                    pointRadius: 2,
                    pointHoverRadius: 4,
                    borderWidth: 2,
                    yAxisID: 'y1'
                }
            );
        } else {
            // Show individual node GPU data
            const nodeUtilizationData = [];
            const nodeMemoryData = [];
            const nodeTemperatureData = [];
            
            sortedMetrics.forEach(metric => {
                let timeLabel;
                if (timeRange === '1h' || timeRange === '6h') {
                    timeLabel = moment(metric.timestamp).format('HH:mm');
                } else if (timeRange === '24h') {
                    timeLabel = moment(metric.timestamp).format('MM/DD HH:mm');
                } else if (timeRange === '7d') {
                    timeLabel = moment(metric.timestamp).format('MM/DD');
                } else {
                    timeLabel = moment(metric.timestamp).format('MM/DD');
                }
                
                if (!timeLabels.includes(timeLabel)) {
                    timeLabels.push(timeLabel);
                }
                
                // Find the specific node data
                const nodeMetric = metric.nodes?.find(node => node.hostname === selectedNode);
                
                let avgUtilization = 0;
                let avgMemoryUtil = 0;
                let avgTemperature = 0;
                
                if (nodeMetric?.system?.gpu?.available && nodeMetric.system.gpu.gpus?.length > 0) {
                    let totalUtil = 0;
                    let totalMemUtil = 0;
                    let totalTemp = 0;
                    const gpuCount = nodeMetric.system.gpu.gpus.length;
                    
                    nodeMetric.system.gpu.gpus.forEach(gpu => {
                        totalUtil += gpu.utilizationPercent || 0;
                        if (gpu.memory?.totalMB > 0) {
                            totalMemUtil += ((gpu.memory.usedMB || 0) / gpu.memory.totalMB) * 100;
                        }
                        totalTemp += gpu.temperatureC || 0;
                    });
                    
                    avgUtilization = gpuCount > 0 ? totalUtil / gpuCount : 0;
                    avgMemoryUtil = gpuCount > 0 ? totalMemUtil / gpuCount : 0;
                    avgTemperature = gpuCount > 0 ? totalTemp / gpuCount : 0;
                }
                
                nodeUtilizationData.push(avgUtilization);
                nodeMemoryData.push(avgMemoryUtil);
                nodeTemperatureData.push(avgTemperature);
            });

            datasets.push(
                {
                    label: `${selectedNode} - GPU Utilization (%)`,
                    data: nodeUtilizationData,
                    borderColor: colors.orange[500],
                    backgroundColor: colors.orange[100],
                    fill: false,
                    pointRadius: 2,
                    pointHoverRadius: 4,
                    borderWidth: 2,
                    yAxisID: 'y'
                },
                {
                    label: `${selectedNode} - Memory Utilization (%)`,
                    data: nodeMemoryData,
                    borderColor: colors.purple[500],
                    backgroundColor: colors.purple[100],
                    fill: false,
                    pointRadius: 2,
                    pointHoverRadius: 4,
                    borderWidth: 2,
                    yAxisID: 'y'
                },
                {
                    label: `${selectedNode} - Temperature (°C)`,
                    data: nodeTemperatureData,
                    borderColor: colors.red[500],
                    backgroundColor: colors.red[100],
                    fill: false,
                    pointRadius: 2,
                    pointHoverRadius: 4,
                    borderWidth: 2,
                    yAxisID: 'y1'
                }
            );
        }

        setChartData({
            labels: timeLabels,
            datasets
        });
    };

    // Fetch data when component mounts or parameters change
    useEffect(() => {
        if (clusterId) {
            const hours = timeRange === '1h' ? 1 : 
                        timeRange === '6h' ? 6 : 
                        timeRange === '24h' ? 24 : 
                        timeRange === '7d' ? 168 : 720;
            
            dispatch(Actions.fetchClusterMetricsHistory(clusterId, hours, 'all'));
        }
    }, [dispatch, clusterId, timeRange]);

    // Process data when history changes
    useEffect(() => {
        if (clusterMetricsHistory) {
            processGpuMetricsData(clusterMetricsHistory);
            if (!hasInitiallyLoaded) {
                setHasInitiallyLoaded(true);
            }
        }
    }, [clusterMetricsHistory, selectedNode, timeRange, hasInitiallyLoaded]);

    // Re-fetch on real-time updates
    useEffect(() => {
        if ((changedDbClusterHealth || changedDbClusters || changedDbNodes) && clusterId) {
            const hours = timeRange === '1h' ? 1 : 
                        timeRange === '6h' ? 6 : 
                        timeRange === '24h' ? 24 : 
                        timeRange === '7d' ? 168 : 720;
            
            dispatch(Actions.fetchClusterMetricsHistory(clusterId, hours, 'all'));
        }
    }, [changedDbClusterHealth, changedDbClusters, changedDbNodes, clusterId, timeRange, dispatch]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
            display: true,
            position: 'top',
            labels: {
                usePointStyle: true,
                padding: 15
            }
        },
        title: {
            display: true,
            text: `GPU Performance Metrics - ${selectedNode === 'all' ? 'Cluster Overview' : selectedNode} (${timeRange})`
        },
        scales: {
            xAxes: [{
                display: true,
                scaleLabel: {
                    display: true,
                    labelString: 'Time'
                },
                ticks: {
                    maxTicksLimit: timeRange === '30d' ? 10 : timeRange === '7d' ? 14 : 20,
                    autoSkip: true
                }
            }],
            yAxes: [{
                id: 'y',
                type: 'linear',
                display: true,
                position: 'left',
                scaleLabel: {
                    display: true,
                    labelString: 'Utilization (%)'
                },
                ticks: {
                    min: 0,
                    max: 100
                }
            }, {
                id: 'y1',
                type: 'linear',
                display: true,
                position: 'right',
                scaleLabel: {
                    display: true,
                    labelString: 'Temperature (°C) / Count'
                },
                ticks: {
                    min: 0
                },
                gridLines: {
                    drawOnChartArea: false
                }
            }]
        },
        tooltips: {
            mode: 'index',
            intersect: false
        },
        hover: {
            mode: 'index',
            intersect: false
        },
        elements: {
            point: {
                radius: 2,
                hoverRadius: 6
            }
        }
    };

    const currentStats = getCurrentGpuStats();

    // NVIDIA-aligned color palette
    const nvidiaColors = {
        emerald: '#76c893',
        keppel: '#52b69a', 
        verdigris: '#34a0a4',
        bondiBlue: '#168aad',
        cerulean: '#1a759f',
        lapisLazuli: '#1e6091',
        indigoDye: '#184e77'
    };

    return (
        <Paper className={className} style={{ padding: 16 }}>
            {/* GPU Stats Summary */}
            {currentStats && (
                <Box marginBottom={3}>
                    <Typography variant="h6" gutterBottom>
                        Current GPU Status
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box 
                                p={2} 
                                style={{ 
                                    backgroundColor: nvidiaColors.emerald + '20', // 20% opacity
                                    borderLeft: `4px solid ${nvidiaColors.emerald}`
                                }}
                                borderRadius={2}
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                            >
                                <Icon style={{ color: nvidiaColors.emerald }}>memory</Icon>
                                <Typography variant="h5" style={{ color: nvidiaColors.emerald, fontWeight: 600 }}>
                                    {currentStats.totalGpus}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Total GPUs
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box 
                                p={2} 
                                style={{ 
                                    backgroundColor: nvidiaColors.keppel + '20', // 20% opacity
                                    borderLeft: `4px solid ${nvidiaColors.keppel}`
                                }}
                                borderRadius={2}
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                            >
                                <Icon style={{ color: nvidiaColors.keppel }}>flash_on</Icon>
                                <Typography variant="h5" style={{ color: nvidiaColors.keppel, fontWeight: 600 }}>
                                    {currentStats.avgUtilization.toFixed(1)}%
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Avg Utilization
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box 
                                p={2} 
                                style={{ 
                                    backgroundColor: nvidiaColors.bondiBlue + '20', // 20% opacity
                                    borderLeft: `4px solid ${nvidiaColors.bondiBlue}`
                                }}
                                borderRadius={2}
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                            >
                                <Icon style={{ color: nvidiaColors.bondiBlue }}>storage</Icon>
                                <Typography variant="h5" style={{ color: nvidiaColors.bondiBlue, fontWeight: 600 }}>
                                    {currentStats.memoryUtilization.toFixed(1)}%
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Memory Usage
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box 
                                p={2} 
                                style={{ 
                                    backgroundColor: nvidiaColors.indigoDye + '20', // 20% opacity
                                    borderLeft: `4px solid ${nvidiaColors.indigoDye}`
                                }}
                                borderRadius={2}
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                            >
                                <Whatshot style={{ color: nvidiaColors.indigoDye }} />
                                <Typography variant="h5" style={{ color: nvidiaColors.indigoDye, fontWeight: 600 }}>
                                    {currentStats.avgTemperature.toFixed(0)}°C
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Avg Temperature
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                    
                    {/* GPU Models */}
                    <Box mt={2}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            GPU Models:
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                            {currentStats.models.map((model, index) => (
                                <Chip 
                                    key={index}
                                    label={model}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    clickable
                                    onClick={(event) => handlePopoverOpen(event, model)}
                                    style={{
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: nvidiaColors.emerald + '10'
                                        }
                                    }}
                                />
                            ))}
                        </Box>

                        {/* GPU Details Popover */}
                        <Popover
                            open={Boolean(popoverAnchorEl)}
                            anchorEl={popoverAnchorEl}
                            onClose={handlePopoverClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'center',
                            }}
                            PaperProps={{
                                style: {
                                    padding: 16,
                                    minWidth: 280,
                                    maxWidth: 400
                                }
                            }}
                        >
                            {selectedGpuModel && (() => {
                                const details = getGpuModelDetails(selectedGpuModel);
                                if (!details) return <Typography>No details available</Typography>;
                                
                                return (
                                    <Box>
                                        <Typography variant="h6" gutterBottom style={{ color: nvidiaColors.emerald }}>
                                            {details.modelName}
                                        </Typography>
                                        
                                        <Grid container spacing={1}>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="textSecondary">
                                                    Count:
                                                </Typography>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {details.count} GPU{details.count > 1 ? 's' : ''}
                                                </Typography>
                                            </Grid>
                                            
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="textSecondary">
                                                    Memory per GPU:
                                                </Typography>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {details.totalMemoryGB} GB
                                                </Typography>
                                            </Grid>
                                            
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="textSecondary">
                                                    Avg Utilization:
                                                </Typography>
                                                <Typography variant="body1" fontWeight={600} style={{ color: nvidiaColors.keppel }}>
                                                    {details.avgUtilization.toFixed(1)}%
                                                </Typography>
                                            </Grid>
                                            
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="textSecondary">
                                                    Temperature:
                                                </Typography>
                                                <Typography variant="body1" fontWeight={600} style={{ color: nvidiaColors.indigoDye }}>
                                                    {details.avgTemperature.toFixed(0)}°C
                                                </Typography>
                                            </Grid>
                                        </Grid>

                                        <Box mt={2}>
                                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                                Nodes with this GPU:
                                            </Typography>
                                            <Box display="flex" gap={1} flexWrap="wrap">
                                                {details.nodes.map((nodeName, idx) => (
                                                    <Chip
                                                        key={idx}
                                                        label={nodeName}
                                                        size="small"
                                                        style={{
                                                            backgroundColor: nvidiaColors.verdigris + '20',
                                                            color: nvidiaColors.verdigris
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>

                                        {details.minTemperature !== details.maxTemperature && (
                                            <Box mt={1}>
                                                <Typography variant="body2" color="textSecondary">
                                                    Temp Range: {details.minTemperature.toFixed(0)}°C - {details.maxTemperature.toFixed(0)}°C
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })()}
                        </Popover>
                    </Box>
                </Box>
            )}

            {/* Chart Controls */}
            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2} paddingBottom={1}>
                <Typography variant="h6">GPU Performance History</Typography>
                
                <Box display="flex" gap={2}>
                    <FormControl size="small" style={{ minWidth: 130 }} variant="outlined">
                        <InputLabel>Time Range</InputLabel>
                        <Select
                            value={timeRange}
                            onChange={(e) => setTimeRange(e.target.value)}
                            label="Time Range"
                        >
                            {timeRangeOptions.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" style={{ minWidth: 140 }} variant="outlined">
                        <InputLabel>GPU Node</InputLabel>
                        <Select
                            value={selectedNode}
                            onChange={(e) => setSelectedNode(e.target.value)}
                            label="GPU Node"
                        >
                            <MenuItem value="all">All GPU Nodes</MenuItem>
                            {getAvailableGpuNodes().map(node => (
                                <MenuItem key={node} value={node}>
                                    {node}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* Chart */}
            <Box height={350}>
                {loading && !hasInitiallyLoaded ? (
                    <Box 
                        display="flex" 
                        flexDirection="column"
                        justifyContent="center" 
                        alignItems="center" 
                        height="100%"
                    >
                        <CircularProgress size={40} />
                        <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                            Loading GPU performance data...
                        </Typography>
                    </Box>
                ) : error ? (
                    <Box 
                        display="flex" 
                        flexDirection="column"
                        justifyContent="center" 
                        alignItems="center" 
                        height="100%"
                    >
                        <Icon color="error" style={{ fontSize: 48, marginBottom: 16 }}>error</Icon>
                        <Typography variant="body1" color="error" style={{ marginBottom: 8 }}>
                            Error loading GPU data
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {error}
                        </Typography>
                    </Box>
                ) : chartData ? (
                    <Line data={chartData} options={chartOptions} />
                ) : (
                    <Box 
                        display="flex" 
                        flexDirection="column"
                        justifyContent="center" 
                        alignItems="center" 
                        height="100%"
                    >
                        <Icon color="disabled" style={{ fontSize: 64, marginBottom: 16 }}>memory</Icon>
                        <Typography variant="body1" color="textSecondary" style={{ marginBottom: 8 }}>
                            No GPU Data Available
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            No GPU metrics found for the selected time range
                        </Typography>
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

export default GpuMetricsChart;
